import React from "react";
import CertificateFooter from "./CertificateFooter";

const SHELL_KEYS = [
  "coating", "corrosion", "dents", "cuts_dig", "leaks",
  "fire_damage", "bulges", "neck_defects", "attachments",
];
const SHELL_LABELS = [
  "Coating", "Corrosion", "Dents", "Cuts, Dig", "Leaks",
  "Fire Damage", "Bulges", "Neck Defects", "Attachments",
];

const formatSlashDate = (value) => {
  if (!value) return "";
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return value;
  const [y, m, d] = value.split("-");
  return `${d}-${m}-${y}`;
};

const formatShortSlashDate = (value) => {
  const full = formatSlashDate(value);
  if (!/^\d{2}-\d{2}-\d{4}$/.test(full)) return full;
  return `${full.slice(0, 6)}${full.slice(8)}`;
};

const isChecked = (value) => value === true || value === "true" || value === 1 || value === "1";

/**
 * Landscape Letter preview for the FM-200 Gas Cylinder report.
 * Shared header / sign-off / footer; no disclaimer band.
 */
const Fm200Document = ({ doc }) => {
  if (!doc) return null;
  const assetsBase = doc.assetsBase || "";
  const logos = doc.footerLogos || [];
  const rows = Array.isArray(doc.cylinder_rows) ? doc.cylinder_rows : [];

  return (
    <div className="cc-pdf-page cc-fm200-page">
      <div className="cc-pdf-header">
        <img className="cc-pdf-logo" src={`${assetsBase}/logo.jpg`} alt="TUV PAK" />
        <div className="cc-pdf-header-text">
          <strong>TUV PAK INSPEC</strong>
          <span>TECHANICAL UNIQUE VISION PAKISTAN INSPECTION &amp; CERTIFICATION</span>
        </div>
      </div>
      <img className="cc-pdf-rule" src={`${assetsBase}/header-bar.png`} alt="" />

      <h4 className="cc-fm200-title">
        {doc.report_title || "FM 200 GAS CYLINDERS PERIODIC VISUAL INSPECTION & HYDROSTATIC TEST REPORT"}
      </h4>

      <div className="cc-fm200-meta">
        <div className="cc-fm200-meta__row">
          <span><b>CERT NO.</b> {doc.report_no}</span>
          <span><b>DATE:</b> {formatSlashDate(doc.issue_date || doc.report_date)}</span>
        </div>
        <div className="cc-fm200-meta__row">
          <span><b>COMPANY:</b> {doc.company}</span>
          <span><b>Valid Till:</b> {formatSlashDate(doc.expire_date || doc.valid_till)}</span>
        </div>
        <div className="cc-fm200-meta__row">
          <span><b>CLIENT:</b> {doc.client}</span>
        </div>
        <div className="cc-fm200-meta__row">
          <span><b>INSPECTED BY:</b> {doc.inspected_by_name}</span>
        </div>
      </div>

      <table className="cc-fm200-table">
        <thead>
          <tr className="cc-fm200-table__band">
            <th colSpan={6 + SHELL_KEYS.length + 3}>INSPECTION SUMMARY</th>
          </tr>
          <tr className="cc-fm200-table__group">
            <th colSpan={6}>CYLINDER IDENTIFICATION</th>
            <th colSpan={SHELL_KEYS.length}>SHELL INSPECTION</th>
            <th colSpan={3}>DISPOSITION</th>
          </tr>
          <tr className="cc-fm200-table__cols">
            <th>Serial No.</th>
            <th>Identification No.</th>
            <th>ICC/DOT Spec.</th>
            <th>MFG</th>
            <th>Date of MFG</th>
            <th>Size/T-Weight</th>
            {SHELL_LABELS.map((label) => (
              <th key={label}>{label}</th>
            ))}
            <th>Disposition</th>
            <th>Inspection Date</th>
            <th>Remarks</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              <td>{index + 1}</td>
              <td>{row.identification_no}</td>
              <td>{row.icc_dot_spec}</td>
              <td>{row.mfg}</td>
              <td>{row.date_of_mfg}</td>
              <td>{row.size_t_weight}</td>
              {SHELL_KEYS.map((key) => (
                <td key={key} className="cc-fm200-check">{isChecked(row[key]) ? "✓" : ""}</td>
              ))}
              <td>{row.disposition}</td>
              <td>{formatShortSlashDate(row.inspection_date)}</td>
              <td>{row.remarks}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {doc.note ? (
        <p className="cc-fm200-note">
          <b>Note:</b> {doc.note}
        </p>
      ) : null}

      <table className="cc-pdf-sign">
        <thead>
          <tr>
            <th>Inspected By</th>
            <th>Approved By</th>
            <th>Company Seal</th>
            <th>QR Code</th>
          </tr>
        </thead>
        <tbody>
          <tr className="cc-pdf-sign-names">
            <td>{doc.inspected_by_name}</td>
            <td>{doc.approved_by_name}</td>
            <td rowSpan={2} className="cc-pdf-sign-seal">
              {doc.sealUrl && <img src={doc.sealUrl} alt="Company seal" />}
            </td>
            <td rowSpan={2} className="cc-pdf-sign-qr">
              {doc.qrDataUrl && <img src={doc.qrDataUrl} alt="QR code" />}
            </td>
          </tr>
          <tr className="cc-pdf-sign-images">
            <td>{doc.inspectedSignatureUrl && <img src={doc.inspectedSignatureUrl} alt="Inspector signature" />}</td>
            <td>{doc.approvedSignatureUrl && <img src={doc.approvedSignatureUrl} alt="Approver signature" />}</td>
          </tr>
        </tbody>
      </table>

      <CertificateFooter
        assetsBase={assetsBase}
        logos={logos}
        website={doc.footer_website}
        email={doc.footer_email}
        gmail={doc.footer_gmail}
      />
    </div>
  );
};

export default Fm200Document;
